
import { Card } from '@/components/ui/card';
import { Table, TableBody } from "@/components/ui/table";
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { LeadsTableProps } from './types';
import { LeadRow } from './table/LeadRow';
import { LeadsTableHeader } from './table/LeadsTableHeader';

export function LeadsTableDesktop({ leads, onLeadUpdate, filters }: LeadsTableProps) {
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  const handleDeleteLead = async (id: string) => {
    try {
      setIsDeleting(id);
      const { error } = await supabase
        .from('nursing_home_leads')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      toast.success('Lead removed successfully');
      // Call onLeadUpdate to refresh the list after deletion
      onLeadUpdate();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Failed to remove lead');
    } finally {
      setIsDeleting(null);
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      setUpdatingStatus(id);
      const { error } = await supabase
        .from('nursing_home_leads')
        .update({ status: newStatus })
        .eq('id', id);
        
      if (error) throw error;
      
      toast.success(`Status updated to ${newStatus}`);
      onLeadUpdate();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (filters.status && lead.status !== filters.status) return false;
    if (filters.county && lead.location !== filters.county) return false;
    return true;
  });

  return (
    <Card>
      <div className="overflow-x-auto">
        <Table>
          <LeadsTableHeader />
          <TableBody>
            {filteredLeads.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-8 text-muted-foreground">
                  No nursing home leads found with the current filters
                </td>
              </tr>
            ) : (
              filteredLeads.map((lead) => (
                <LeadRow
                  key={lead.id}
                  lead={lead}
                  isDeleting={isDeleting}
                  updatingStatus={updatingStatus}
                  onDelete={handleDeleteLead}
                  onStatusChange={handleStatusChange}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
